import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Observable, ReplaySubject, catchError, map, of, tap } from 'rxjs';
import { Navigation } from 'app/core/navigation/navigation.types';
import { PermissionService } from 'app/core/permissions/permission.service';
import {
    compactNavigation,
    defaultNavigation,
    futuristicNavigation,
    horizontalNavigation,
} from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';

type PermissionNavigationItem = FuseNavigationItem & {
    requiredPermissionCode?: string;
    children?: PermissionNavigationItem[];
};

@Injectable({
    providedIn: 'root'
})
export class NavigationService
{
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    /**
     * Constructor
     */
    constructor(
        private _permissionService: PermissionService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation>
    {
        return this._permissionService.loadAccess().pipe(
            map((access) => this.buildNavigation(access.navigation)),
            catchError(() => {
                const fallbackNavigation = cloneDeep(defaultNavigation);
                this._permissionService.setPermissions(this.extractPermissionCodes(fallbackNavigation));
                this._permissionService.setNavigation(fallbackNavigation);
                return of(this.buildNavigation(fallbackNavigation));
            }),
            tap((navigation) => {
                this._navigation.next(navigation);
            })
        );
    }

    setNavigation(defaultItems: Navigation["default"]): void
    {
        this._navigation.next(this.buildNavigation(defaultItems));
    }

    private buildNavigation(defaultItems: Navigation["default"]): Navigation
    {
        return {
            default: cloneDeep(defaultItems),
            compact: this.fillNavigationChildren(compactNavigation, defaultItems),
            futuristic: this.fillNavigationChildren(futuristicNavigation, defaultItems),
            horizontal: this.fillNavigationChildren(horizontalNavigation, defaultItems),
        };
    }

    private fillNavigationChildren(source: Navigation["default"], defaultItems: Navigation["default"]): Navigation["default"]
    {
        const items = cloneDeep(source);

        items.forEach((item) => {
            const defaultItem = defaultItems.find((defaultNavItem) => defaultNavItem.id === item.id);
            if (defaultItem) {
                item.children = cloneDeep(defaultItem.children);
            }
        });

        return items;
    }

    private extractPermissionCodes(items: PermissionNavigationItem[]): string[]
    {
        return items.flatMap((item) => {
            const permissionCode = item.requiredPermissionCode ? [item.requiredPermissionCode] : [];
            const childPermissionCodes = item.children ? this.extractPermissionCodes(item.children) : [];
            return [...permissionCode, ...childPermissionCodes];
        });
    }
}
